<script setup lang="ts">

interface Metric {
    key: string;
    label: string;
    value: string;
}

interface Card {
    id: string;
    title: string;
    subtitle: string;
    score: number;
    metricsTitle: string;
    metrics: Metric[];
}

interface Props {
    card: Card;
    isCompact?: boolean;
    isMobile?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    isCompact: false,
    isMobile: false
});

const circleSize = computed(() => props.isMobile ? 100 : props.isCompact ? 120 : 150);
const circleRadius = computed(() => props.isMobile ? 40 : props.isCompact ? 50 : 60);
const strokeWidth = computed(() => props.isMobile ? 6 : 8);
const circumference = computed(() => 2 * Math.PI * circleRadius.value);
const strokeDashoffset = computed(() => circumference.value - (props.card.score / 100) * circumference.value);
</script>

<template>
    <div>
        <!-- Score Section -->
        <div :class="`flex items-center justify-center ${isMobile ? 'flex-col gap-2' : 'gap-4'} mb-6`">
            <div class="relative">
                <svg :width="circleSize" :height="circleSize" :viewBox="`0 0 ${circleSize} ${circleSize}`"
                    class="transform -rotate-90">
                    <circle :cx="circleSize / 2" :cy="circleSize / 2" :r="circleRadius" stroke="currentColor"
                        :stroke-width="strokeWidth" fill="none" class="text-slate-700" />
                    <circle :cx="circleSize / 2" :cy="circleSize / 2" :r="circleRadius" stroke="currentColor"
                        :stroke-width="strokeWidth" fill="none" class="text-green-400 transition-all duration-1000"
                        :stroke-dasharray="circumference" :stroke-dashoffset="strokeDashoffset"
                        stroke-linecap="round" />
                </svg>
                <div class="absolute inset-0 flex items-center justify-center">
                    <span
                        :class="`text-white font-bold ${isMobile ? 'text-3xl' : isCompact ? 'text-4xl' : 'text-5xl'}`">
                        {{ card.score }}
                    </span>
                </div>
            </div>
            <div :class="isMobile ? 'text-center' : ''">
                <h2 :class="`text-white font-bold ${isMobile ? 'text-2xl' : isCompact ? 'text-3xl' : 'text-4xl'}`">
                    {{ card.title }}
                </h2>
            </div>
        </div>

        <!-- Metrics -->
        <div class="mb-6">
            <div class="h-px bg-slate-600 mb-4"></div>
            <h3 :class="`text-white text-left font-bold ${isMobile ? 'text-lg' : 'text-xl'}`">
                {{ card.metricsTitle }}
            </h3>
            <div class="h-px bg-slate-600 mt-4"></div>
        </div>

        <div
            :class="`grid ${isMobile ? 'grid-cols-1 gap-4' : isCompact ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-x-32 gap-y-6'}`">
            <div v-for="metric in card.metrics" :key="metric.key" class="flex gap-2">
                <div class="w-4 h-4 bg-green-400 rounded-full flex-shrink-0 mt-1"></div>
                <div class="flex text-left flex-col gap-1">
                    <span :class="`text-gray-300 ${isMobile ? 'text-sm' : 'text-base'}`">
                        {{ metric.label }}
                    </span>
                    <div :class="`text-white font-bold ${isMobile ? 'text-xl' : isCompact ? 'text-2xl' : 'text-3xl'}`">
                        {{ metric.value }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
